(function () {
  // Desktop (fine pointer) opens a marker on hover; coarse pointers toggle it on the dot.
  // Evaluated per event so a hybrid device stays correct — the same split as feature-hotspots.
  const hoverQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
  // Mirrors the product card's "quick view on mobile" breakpoint ($sm).
  const mobileQuery = window.matchMedia("(max-width: 575.98px)");
  // The product list is desktop-only ($lg); CSS hides it below.
  const listQuery = window.matchMedia("(min-width: 990px)");

  class ProductMarkers {
    constructor(section) {
      this.section = section;
      this.controller = new AbortController();
      this.hovered = null;
      this.pendingTab = null;
      this.groups = Array.from(
        section.querySelectorAll(".product-markers__group")
      );
      this.markers = Array.from(section.querySelectorAll("[data-marker]"));

      const { signal } = this.controller;

      // Hover follows real pointer movement only: a card growing under a resting cursor
      // makes Chrome fire mouseenter/mouseleave with no mousemove.
      section.addEventListener(
        "mousemove",
        (event) => {
          if (!hoverQuery.matches) return;

          const target = event.target;
          const marker =
            target instanceof Element ? target.closest("[data-marker]") : null;
          // On phones a marker either opens its quick view or stays closed — no card to hover open.
          const hoverable =
            marker && this.markers.includes(marker) && !this._skipsCard(marker);
          this._setHovered(hoverable ? marker : null);
        },
        { signal }
      );

      section.addEventListener(
        "mouseleave",
        () => {
          if (hoverQuery.matches) this._setHovered(null);
        },
        { signal }
      );

      this.markers.forEach((marker) => this._initMarker(marker, signal));
      this.groups.forEach((group) => this._initList(group, signal));
      this._makeTabbable();

      this._syncDisabled();
      mobileQuery.addEventListener("change", () => this._syncDisabled(), {
        signal,
      });

      // The card's height depends on the viewport.
      window.addEventListener(
        "resize",
        () => {
          this.markers.forEach((marker) => {
            if (marker.classList.contains("is-open")) this._layout(marker);
          });
        },
        { signal }
      );

      // A list left open on desktop must not come back open after a trip below $lg.
      listQuery.addEventListener(
        "change",
        () => {
          if (!listQuery.matches) {
            this.groups.forEach((group) => this._closeList(group));
          }
        },
        { signal }
      );

      document.addEventListener(
        "click",
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest("[data-list-toggle]")) return;
          // A quick view opened from the list lives in <body>: working in it must not
          // dismiss the list it was opened from.
          if (target.closest("quick-view-modal")) return;

          if (!hoverQuery.matches && !target.closest("[data-marker]")) {
            this._closeAll();
          }
          if (!target.closest(".product-markers__list")) {
            this.groups.forEach((group) => this._closeList(group));
          }
        },
        { signal }
      );

      // An open list keeps Tab inside itself and its close control, so the only way out
      // is the close button (or Escape). The quick view it opens runs its own trap.
      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Tab") return;
          if (document.querySelector("quick-view-modal[open]")) return;

          const group = this.groups.find((item) =>
            item.querySelector("[data-list-modal].is-open")
          );
          if (!group) return;

          this._trapListTab(group, event);
          // Remembered for the focusin guard below, for this one keystroke only.
          this.pendingTab = { group, backwards: event.shiftKey };
          setTimeout(() => {
            this.pendingTab = null;
          });
        },
        { signal }
      );

      // Safety net for a Tab stop the trap did not foresee: focus that still lands outside
      // the open list is sent back to its start (or its end on Shift+Tab). Capture phase,
      // and the event stops here, so the marker it landed on never opens its card.
      document.addEventListener(
        "focusin",
        (event) => {
          if (!this.pendingTab) return;

          const { group, backwards } = this.pendingTab;
          const listToggle = group.querySelector("[data-list-toggle]");
          const list = group.querySelector("[data-list-modal]");
          const target = event.target;
          if (!listToggle || !list || !list.classList.contains("is-open"))
            return;
          if (!(target instanceof Element)) return;
          if (target === listToggle || list.contains(target)) return;
          if (target.closest("quick-view-modal")) return;

          event.stopPropagation();
          const stops = this._tabStops(list);
          const destination = backwards
            ? stops[stops.length - 1] || listToggle
            : listToggle;
          destination.focus();
        },
        { capture: true, signal }
      );

      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Escape") return;
          // Escape closes an open quick view first; the list behind it stays.
          if (document.querySelector("quick-view-modal[open]")) return;
          this._closeAll();
          this.groups.forEach((group) => this._closeList(group, true));
        },
        { signal }
      );
    }

    _initMarker(marker, signal) {
      const toggle = marker.querySelector("[data-marker-toggle]");
      if (!toggle) return;

      // Keyboard focus only — a pointer press also fires focusin and would fight the click toggle.
      marker.addEventListener(
        "focusin",
        (event) => {
          const focused = event.target;
          if (!(focused instanceof Element)) return;

          try {
            if (focused.matches(":focus-visible") && !this._skipsCard(marker)) {
              this._open(marker);
            }
          } catch (error) {
            /* :focus-visible unsupported — hover and click still cover it */
          }
        },
        { signal }
      );

      marker.addEventListener(
        "focusout",
        (event) => {
          if (!marker.contains(event.relatedTarget)) this._close(marker);
        },
        { signal }
      );

      toggle.addEventListener(
        "click",
        () => {
          // On phones a marker with a product goes straight to its quick view, and one
          // without a product does nothing. With quick view off on phones the card opens.
          if (this._opensQuickView(marker)) {
            this._openQuickView(marker, toggle, marker.dataset.productUrl);
            return;
          }
          if (this._skipsCard(marker) || hoverQuery.matches) return;

          if (marker.classList.contains("is-open")) {
            this._close(marker);
          } else {
            this._open(marker);
          }
        },
        { signal }
      );

      // A colour swatch (or the "+N" link) opens the product's quick view on that variant.
      // Without the modal — quick view off, or off on phones — it stays a plain link.
      marker.addEventListener(
        "click",
        (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;

          const link = target.closest(
            ".product-card-horizontal__color-swatches a[href]"
          );
          if (!link || !marker.contains(link)) return;
          if (this._openQuickView(marker, link, link.getAttribute("href"))) {
            event.preventDefault();
          }
        },
        { signal }
      );
    }

    // Safari's default Tab order stops only on form fields and an explicit tabindex, so
    // it skipped the dots, the list toggle and every link in the cards and the list.
    // Swiper sets its own nav buttons' tabindex; the closed list stays out through inert.
    _makeTabbable() {
      const scopes = this.section.querySelectorAll(
        "[data-marker], [data-list-modal]"
      );
      const controls = [
        ...this.section.querySelectorAll("[data-list-toggle]"),
        ...Array.from(scopes).flatMap((scope) =>
          Array.from(scope.querySelectorAll("a[href], button"))
        ),
      ];

      controls.forEach((control) => {
        if (control.hasAttribute("tabindex")) return;
        if (control.classList.contains("slider-nav-button")) return;
        control.setAttribute("tabindex", "0");
      });
    }

    // An empty marker does nothing on phones, so its dot is announced as disabled there.
    _syncDisabled() {
      this.markers.forEach((marker) => {
        if (!marker.hasAttribute("data-placeholder")) return;

        const toggle = marker.querySelector("[data-marker-toggle]");
        if (!toggle) return;

        if (mobileQuery.matches) {
          toggle.setAttribute("aria-disabled", "true");
        } else {
          toggle.removeAttribute("aria-disabled");
        }
      });
    }

    // On phones neither a marker that opens its quick view nor an empty one opens a card.
    _skipsCard(marker) {
      return (
        this._opensQuickView(marker) ||
        (mobileQuery.matches && marker.hasAttribute("data-placeholder"))
      );
    }

    // On phones a marker with a product (and quick view on for phones) skips its card.
    _opensQuickView(marker) {
      return (
        mobileQuery.matches &&
        Boolean(marker.dataset.productUrl) &&
        Boolean(marker.dataset.quickView) &&
        marker.dataset.quickViewMobile !== "false"
      );
    }

    // The modal fetches whatever its opener names: a swatch names its own variant, the
    // marker's dot the product itself.
    _openQuickView(marker, opener, url) {
      if (!url) return false;
      const selector = marker.dataset.quickView;
      if (!selector) return false;
      if (marker.dataset.quickViewMobile === "false" && mobileQuery.matches) {
        return false;
      }

      const modal = document.querySelector(selector);
      if (!modal || typeof modal.show !== "function") return false;

      opener.dataset.productUrl = url;
      modal.show(opener);
      return true;
    }

    _initList(group, signal) {
      const listToggle = group.querySelector("[data-list-toggle]");
      const list = group.querySelector("[data-list-modal]");
      if (!listToggle || !list) return;

      listToggle.addEventListener(
        "click",
        () => {
          if (list.classList.contains("is-open")) {
            this._closeList(group, true);
          } else {
            this._openList(group);
          }
        },
        { signal }
      );

      // The pointer that opened the list still rests on the cross; the hover turn waits
      // until it has left once, so it plays on the next hover instead of on open.
      listToggle.addEventListener(
        "mouseleave",
        () => listToggle.classList.remove("is-hover-held"),
        { signal }
      );
    }

    _setHovered(marker) {
      if (marker === this.hovered) return;

      const previous = this.hovered;
      this.hovered = marker;

      if (marker) {
        this._open(marker);
      } else if (previous) {
        this._close(previous);
      }
    }

    _open(marker) {
      this._closeAll(marker);
      this._layout(marker);
      marker.classList.add("is-open");
      const toggle = marker.querySelector("[data-marker-toggle]");
      if (toggle) toggle.setAttribute("aria-expanded", "true");
    }

    // The box grows to the card's own height: a long name, a sale price or swatches make
    // the card taller than its default, and a fixed box would clip them.
    _layout(marker) {
      const card = marker.querySelector(".product-markers__marker-card");
      if (!card) return;

      marker.style.setProperty(
        "--product-markers-open-height",
        `${card.offsetHeight}px`
      );
    }

    _close(marker) {
      if (marker === this.hovered) this.hovered = null;
      marker.classList.remove("is-open");
      const toggle = marker.querySelector("[data-marker-toggle]");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
    }

    _closeAll(except) {
      this.markers.forEach((marker) => {
        if (marker !== except) this._close(marker);
      });
    }

    _openList(group) {
      const listToggle = group.querySelector("[data-list-toggle]");
      const list = group.querySelector("[data-list-modal]");
      if (!listToggle || !list) return;

      this._closeAll();
      // Inert while closed: the slider bullets inside set their own visibility, so the
      // hidden panel alone would still let Tab reach them.
      list.inert = false;
      list.classList.add("is-open");
      if (listToggle.matches(":hover"))
        listToggle.classList.add("is-hover-held");
      listToggle.setAttribute("aria-expanded", "true");
      // Focus lands on the opener, now the panel's close control — the first stop of the
      // loop, with the product cards next. Only moved when it is not there already
      // (Safari does not focus a clicked button): a programmatic focus can draw the ring
      // after a mouse click.
      if (document.activeElement !== listToggle) listToggle.focus();
    }

    // What Tab actually stops on, so the trap's ends are reachable: a radio group is one
    // stop (its checked radio, or the first when none is), and a visibility-hidden element
    // is none. The shared helper lists every radio, so a last unchecked swatch was a
    // boundary Tab never reached, and focus left the panel from the checked one.
    _tabStops(container) {
      const all =
        typeof getFocusableElements === "function"
          ? getFocusableElements(container)
          : [];
      const isRadio = (element) =>
        element instanceof HTMLInputElement &&
        element.type === "radio" &&
        Boolean(element.name);

      return all.filter((element) => {
        if (getComputedStyle(element).visibility === "hidden") return false;
        if (!isRadio(element)) return true;

        const radios = all.filter(
          (other) =>
            isRadio(other) &&
            other.name === element.name &&
            other.form === element.form
        );
        return element === (radios.find((radio) => radio.checked) || radios[0]);
      });
    }

    // The close control sits before the panel in the DOM, so the loop runs
    // close button → panel contents → back to the close button.
    _trapListTab(group, event) {
      const listToggle = group.querySelector("[data-list-toggle]");
      const list = group.querySelector("[data-list-modal]");
      if (!listToggle || !list) return;

      const focusable = this._tabStops(list);
      const first = listToggle;
      const last = focusable[focusable.length - 1] || listToggle;
      const target = event.target;
      const inside = target === listToggle || list.contains(target);

      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && target === first) {
        event.preventDefault();
        last.focus();
      } else if (event.shiftKey && target === list) {
        event.preventDefault();
        first.focus();
      } else if (
        !event.shiftKey &&
        (target === last || (target === list && !focusable.length))
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    // Focus only returns to the opener when the panel was dismissed from the
    // keyboard or the control itself — an outside click must not move it.
    _closeList(group, restoreFocus) {
      const listToggle = group.querySelector("[data-list-toggle]");
      const list = group.querySelector("[data-list-modal]");
      if (!listToggle || !list || !list.classList.contains("is-open")) return;

      list.classList.remove("is-open");
      listToggle.classList.remove("is-hover-held");
      list.inert = true;
      listToggle.setAttribute("aria-expanded", "false");
      if (restoreFocus) listToggle.focus();
    }

    destroy() {
      this.controller.abort();
      this._closeAll();
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains("product-markers-section")) {
      return;
    }

    const root = section.querySelector(".product-markers");
    if (!root) return;

    if (root._productMarkersInstance) {
      root._productMarkersInstance.destroy();
      root._productMarkersInstance = null;
    }
    root._productMarkersInstance = new ProductMarkers(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeProductMarkersEvents) {
    window.__themeProductMarkersEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(".product-markers").forEach((root) => {
        if (!root._productMarkersInstance) return;

        root._productMarkersInstance.destroy();
        root._productMarkersInstance = null;
      });
    });
  }
})();
